/**
 * Centralized analytics constants for the snippets package.
 *
 * Organized into three sections:
 *   1. Shared    – source, event types, origins, page modes (used by all events)
 *   2. UI        – subjects, actions, subject IDs, modal outcomes (sendUIEvent)
 *   3. Operational – subjects, actions (sendOperationalEvent)
 */

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Shared
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Event type used to dispatch analytics events */
export enum ANALYTICS_EVENT_TYPE {
	SEND_UI_EVENT = 'sendUIEvent',
	SEND_OPERATIONAL_EVENT = 'sendOperationalEvent',
	SEND_TRACK_EVENT = 'sendTrackEvent',
}

/** The data.type value inside an analytics event payload */
export enum ANALYTICS_TYPE {
	UI = 'ui',
	OPERATIONAL = 'operational',
}

/** Single source for all snippets analytics events */
export const ANALYTICS_SOURCE = 'confluenceSnippets';

/** Identifies which component/surface fired the event */
export enum ANALYTICS_ORIGIN {
	// snippet CRUD modals
	CREATE_SNIPPET_MODAL = 'createSnippetModal',
	EDIT_SNIPPET_MODAL = 'editSnippetModal',
	DUPLICATE_SNIPPET_MODAL = 'duplicateSnippetModal',
	DELETE_SNIPPET_MODAL = 'deleteSnippetModal',
	// onboarding
	FIRST_SNIPPET_CREATED_MODAL = 'firstSnippetCreatedModal',
	// surfaces
	SNIPPET_PANEL = 'snippetPanel',
	SNIPPET_GALLERY = 'snippetGallery',
	// toolbars / menus
	CONTEXTUAL_TOOLBAR = 'contextualToolbar',
	BLOCK_MENU = 'blockMenu',
	QUICK_INSERT = 'quickInsert',
}

export type AnalyticsOrigin = ANALYTICS_ORIGIN;

export enum ANALYTICS_CREATE_SOURCE {
	EMPTY_PANEL = 'snippetPanelEmpty',
	SNIPPET_PANEL_LIST = 'snippetPanelCreateItem',
	HIGHLIGHT_VIEW = 'snippetHighlightView',
	HIGHLIGHT_EDIT = 'snippetHighlightEdit',
}

/** Identifies the page mode when the analytics event was fired */
export enum ANALYTICS_PAGE_MODE {
	VIEW = 'view',
	EDIT = 'edit',
	LIVE = 'live',
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  UI events (sendUIEvent)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Action subjects – the type of UI element (e.g. button, modal) */
export enum ANALYTICS_UI_SUBJECT {
	BUTTON = 'button',
	MODAL = 'modal',
	TEXT_FIELD = 'textField',
	DROPDOWN_ITEM = 'dropdownItem',
	SNIPPETS = 'snippets',
}

/** Actions – the user interaction that occurred */
export enum ANALYTICS_UI_ACTION {
	CLICKED = 'clicked',
	CLOSED = 'closed',
	TYPED = 'typed',
	VIEWED = 'viewed',
}

/** Action subject IDs – the specific UI element involved */
export enum ANALYTICS_UI_SUBJECT_ID {
	// awareness / screen-viewed (impression)
	SNIPPETS = 'snippets',
	// button
	CREATE_SNIPPET = 'createSnippet',
	DELETE_SNIPPET = 'deleteSnippet',
	EDIT_SNIPPET = 'editSnippet',
	DUPLICATE_SNIPPET = 'duplicateSnippet',
	INSERT_SNIPPET = 'insertSnippet',
	COPY_SNIPPET_LINK = 'copySnippetLink',
	USE_SNIPPET_DETAIL_PANEL = 'useSnippetDetailPanel',
	SNIPPET_FAVORITE_TOGGLED = 'snippetFavoriteToggled',
	// textField / button / dropdownItem (filter-related)
	SNIPPET_CATEGORY_FILTER = 'snippetCategoryFilter',
	SNIPPET_SEARCH = 'snippetSearch',
	SNIPPET_SORT_ORDER = 'snippetSortOrder',
	// FirstSnippetCreatedModal (use attributes for viewed vs dismissed and dismissalAction)
	FIRST_SNIPPET_CREATED_MODAL = 'firstSnippetCreatedModal',
	// Empty state primary actions
	EMPTY_MY_TEMPLATES_CALL_TO_ACTION = 'emptyMyTemplatesCallToAction',
	EMPTY_FAVORITES_CALL_TO_ACTION = 'emptyFavoritesCallToAction',
}

/** Modal type — identifies which save modal fired the event */
export enum MODAL_TYPE {
	CREATE = 'CREATE',
	EDIT = 'EDIT',
	DUPLICATE = 'DUPLICATE',
}

/** Outcome values for modal analytics events (passed in attributes.outcome) */
export enum MODAL_OUTCOME {
	CREATED = 'created',
	EDITED = 'edited',
	DUPLICATED = 'duplicated',
	DELETED = 'deleted',
	CANCELLED = 'cancelled',
	CLOSED = 'closed',
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Operational events (sendOperationalEvent)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Action subjects for operational events */
export enum ANALYTICS_OPERATIONAL_SUBJECT {
	SNIPPET_FAVORITE_TOGGLED = 'snippetFavoriteToggled',
	INSERT_SNIPPET = 'insertSnippet',
	// API failures
	SNIPPET_API_CREATE = 'snippetApiCreate',
	SNIPPET_API_DUPLICATE = 'snippetApiDuplicate',
	SNIPPET_API_UPDATE = 'snippetApiUpdate',
	SNIPPET_API_DELETE = 'snippetApiDelete',
	SNIPPET_API_FETCH = 'snippetApiFetch',
	SNIPPET_API_SHARE = 'snippetApiShare',
	SNIPPET_API_UNSHARE = 'snippetApiUnshare',
	// API successes
	SNIPPET_API_SAVE_SUCCESS = 'snippetApiSaveSuccess',
	// AI metadata
	SNIPPET_API_AI_METADATA = 'snippetApiAiMetadata',
	// Client-side errors
	SNIPPET_ADF_SERIALIZATION = 'snippetAdfSerialization',
	SNIPPET_PREVIEW_RENDER = 'snippetPreviewRender',
}

/** Actions for operational events */
export enum ANALYTICS_OPERATIONAL_ACTION {
	FAILED = 'failed',
	SUCCEEDED = 'succeeded',
	FETCHED = 'fetched',
}

/**
 * Reason attached to a genuine `insertSnippet failed` operational event so real
 * insert failures are diagnosable in analytics. Benign not-ready / empty-body
 * cases are NOT emitted as failures and therefore have no reason here.
 */
export enum INSERT_SNIPPET_FAILURE_REASON {
	/** An exception was thrown while executing the editor insert transaction. */
	INSERT_EXCEPTION = 'insertException',
}

/**
 * Categorical reason on a `snippetApiCreate failed` event, so create failures
 * group by cause instead of free text. The raw message is never emitted (PII);
 * a numeric `statusCode` is sent alongside when available.
 */
export enum CREATE_SNIPPET_FAILURE_REASON {
	/** The create request was rejected because the body exceeded the size limit (HTTP 400). */
	BODY_TOO_LARGE = 'bodyTooLarge',
	/** The create succeeded server-side but the returned payload could not be transformed. */
	TRANSFORM_FAILED = 'transformFailed',
	/** The Block Service rejected the create request with an HTTP error status. */
	CREATE_EXCEPTION = 'createException',
	/** Any other / unclassified create failure (e.g. a non-HTTP runtime error). */
	UNKNOWN = 'unknown',
}

export type SnippetCreateFailureReason = CREATE_SNIPPET_FAILURE_REASON;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Track events (sendTrackEvent)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Action subjects for track events */
export enum ANALYTICS_TRACK_SUBJECT {
	SNIPPET_API_SHARE_CLAIM = 'snippetApiShareClaim',
}

/** Actions for track events */
export enum ANALYTICS_TRACK_ACTION {
	CLAIMED = 'claimed',
}
